import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { PersonaBookmark, ImpersonationStorageState } from "@dugads/shared";
import type { UserPublic } from "@dugads/shared";
import { setImpersonationUserId } from "./api";
import { useAuth } from "./auth-context";
import { PERSONA_STORAGE_KEY } from "./client-storage";

interface ImpersonationState {
  isImpersonating: boolean;
  activePersona: PersonaBookmark | null;
  personas: PersonaBookmark[];
  canImpersonate: boolean;
  realUserSnapshot: UserPublic | null;
  startImpersonation: (bookmark: PersonaBookmark) => Promise<void>;
  exitImpersonation: () => Promise<void>;
  addPersona: (bookmark: PersonaBookmark) => void;
  removePersona: (userId: string) => void;
  isPersonaSaved: (userId: string) => boolean;
}

const ImpersonationContext = createContext<ImpersonationState | null>(null);

const STORAGE_KEY = PERSONA_STORAGE_KEY;

function loadState(): ImpersonationStorageState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as ImpersonationStorageState;
  } catch { /* ignore */ }
  return { activeUserId: null, personas: [] };
}

function saveState(s: ImpersonationStorageState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { user: authUser, refreshUser } = useAuth();
  const [storageState, setStorageState] = useState<ImpersonationStorageState>(loadState);

  // Snapshot of the real admin's identity -- only updated when NOT impersonating,
  // so it always reflects the true logged-in user even when authUser is overridden.
  // Using state (not refs) so canImpersonate re-computes reactively when auth loads.
  const [realUserSnapshot, setRealUserSnapshot] = useState<UserPublic | null>(null);
  const [realRoles, setRealRoles] = useState<Array<{ role_type: string }>>([]);

  useEffect(() => {
    if (!storageState.activeUserId && authUser) {
      setRealUserSnapshot(authUser.user);
      setRealRoles(authUser.roles);
    }
  }, [authUser, storageState.activeUserId]);

  // On mount: re-hydrate the module-level variable from localStorage so that
  // the very first getMe() call (triggered by onAuthStateChanged) already carries
  // the impersonation header, making page refresh fully transparent.
  useEffect(() => {
    setImpersonationUserId(storageState.activeUserId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only

  // Once the real Firebase user resolves, if we have a stored activeUserId,
  // re-fetch /me to populate impersonated user context.
  const resolvedRealUserId = authUser?.user.id;
  useEffect(() => {
    if (resolvedRealUserId && storageState.activeUserId) {
      refreshUser().catch(() => {
        // If /me fails (e.g. target deleted), clear impersonation gracefully
        setImpersonationUserId(null);
        setStorageState((prev) => {
          const next = { ...prev, activeUserId: null };
          saveState(next);
          return next;
        });
      });
    }
  }, [resolvedRealUserId]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: only when real user resolves

  const canImpersonate = useMemo(() => {
    return realRoles.some(
      (r) => r.role_type === "super_admin" || r.role_type === "org_admin",
    );
  }, [realRoles]);

  const startImpersonation = useCallback(async (bookmark: PersonaBookmark) => {
    setImpersonationUserId(bookmark.userId);
    const next: ImpersonationStorageState = { ...storageState, activeUserId: bookmark.userId };
    saveState(next);
    setStorageState(next);
    try {
      await refreshUser();
    } catch {
      // Server rejected impersonation -- roll back
      setImpersonationUserId(null);
      const rolled: ImpersonationStorageState = { ...next, activeUserId: null };
      saveState(rolled);
      setStorageState(rolled);
      throw new Error("Impersonation denied by server");
    }
  }, [storageState, refreshUser]);

  const exitImpersonation = useCallback(async () => {
    setImpersonationUserId(null);
    const next: ImpersonationStorageState = { ...storageState, activeUserId: null };
    saveState(next);
    setStorageState(next);
    await refreshUser().catch(() => { /* best-effort */ });
  }, [storageState, refreshUser]);

  const addPersona = useCallback((bookmark: PersonaBookmark) => {
    setStorageState((prev) => {
      if (prev.personas.some((p) => p.userId === bookmark.userId)) return prev;
      const next = { ...prev, personas: [...prev.personas, bookmark] };
      saveState(next);
      return next;
    });
  }, []);

  const removePersona = useCallback((userId: string) => {
    setStorageState((prev) => {
      const wasActive = prev.activeUserId === userId;
      if (wasActive) setImpersonationUserId(null);
      const next: ImpersonationStorageState = {
        personas: prev.personas.filter((p) => p.userId !== userId),
        activeUserId: wasActive ? null : prev.activeUserId,
      };
      saveState(next);
      if (wasActive) {
        refreshUser().catch(() => { /* best-effort */ });
      }
      return next;
    });
  }, [refreshUser]);

  const isPersonaSaved = useCallback(
    (userId: string) => storageState.personas.some((p) => p.userId === userId),
    [storageState.personas],
  );

  const activePersona = storageState.activeUserId
    ? (storageState.personas.find((p) => p.userId === storageState.activeUserId) ?? null)
    : null;

  return (
    <ImpersonationContext.Provider value={{
      isImpersonating: storageState.activeUserId !== null,
      activePersona,
      personas: storageState.personas,
      canImpersonate,
      realUserSnapshot,
      startImpersonation,
      exitImpersonation,
      addPersona,
      removePersona,
      isPersonaSaved,
    }}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation(): ImpersonationState {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error("useImpersonation must be used within ImpersonationProvider");
  return ctx;
}
